import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { showToast } from '../../../components/Toast';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { usePermesso } from '../../../context/PermessiContext';
import { Kpi, Prese, StatoColonnina } from './componenti';
import GuidaCollegamento from './GuidaCollegamento';
import {
  ElencoColonnine, MOD_COLONNINE, PERCORSO_COLONNINE, SedeOpzione,
  formatDataOra, messaggioErrore,
} from './tipi';
import { API_DOCS } from './ColonnineRicaricaPage.api';

interface FormNuova {
  nome: string;
  codiceOcpp: string;
  sedeId: string;
}

const FORM_VUOTO: FormNuova = { nome: '', codiceOcpp: '', sedeId: '' };
const URL_GATEWAY = 'wss://ocpp.pianinvest.it';
const REGEX_CODICE_OCPP = /^[A-Z0-9_-]{3,40}$/;

export default function ColonnineRicaricaPage() {
  const navigate = useNavigate();
  const { can } = usePermesso();
  const canEdit = can(MOD_COLONNINE, 'edit');

  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [dati, setDati] = useState<ElencoColonnine | null>(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [fQ, setFQ] = useState('');

  // Modale nuova colonnina
  const [form, setForm] = useState<FormNuova | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [erroreForm, setErroreForm] = useState('');
  const [sedi, setSedi] = useState<SedeOpzione[]>([]);
  const [salvando, setSalvando] = useState(false);

  const isDirty = form !== null && JSON.stringify(form) !== initialSnapshot;
  useDirtyForm(isDirty, 'Nuova colonnina');

  // Scarta le risposte di richieste superate (cambio società mentre si carica)
  const richiesta = useRef(0);

  const carica = useCallback(async () => {
    if (!pronta) return;
    const numero = ++richiesta.current;
    setLoading(true);
    setErrore('');
    try {
      const qs = societaId ? `?societaId=${societaId}` : '';
      // @api GET /api/ricarica/colonnine?societaId -> {success:true,data:{gateway:{raggiungibile,ultimoContattoAt},colonnine:[{id,societaId,societaNome,nome,codiceOcpp,sede,marca,modello,seriale,firmware,collegata,ultimoContattoAt,stato,codiceErrore,attiva,passwordImpostata,connettori}]}}
      const res = await apiFetch<{ success: boolean; data: ElencoColonnine }>(`/api/ricarica/colonnine${qs}`);
      if (numero !== richiesta.current) return;
      setDati(res.data);
    } catch (err) {
      if (numero !== richiesta.current) return;
      setErrore(messaggioErrore(err, 'Errore nel caricamento delle colonnine'));
    } finally {
      if (numero === richiesta.current) setLoading(false);
    }
  }, [pronta, societaId]);

  useEffect(() => { carica(); }, [carica]);

  const gatewayOk = dati?.gateway.raggiungibile ?? false;

  const visibili = useMemo(() => {
    const q = fQ.trim().toLowerCase();
    const colonnine = dati?.colonnine ?? [];
    if (!q) return colonnine;
    return colonnine.filter((c) =>
      `${c.nome} ${c.codiceOcpp} ${c.sede?.nome ?? ''}`.toLowerCase().includes(q));
  }, [dati, fQ]);

  const prese = visibili.flatMap((c) => c.connettori);
  const collegate = visibili.filter((c) => c.collegata).length;
  const nomeSocieta = listaSocieta.find((s) => String(s.id) === societaId)?.nome ?? '';

  // ── Nuova colonnina ────────────────────────────────────────────────────────
  const apriNuova = async () => {
    if (!societaId) return;
    setErroreForm('');
    setForm(FORM_VUOTO);
    setInitialSnapshot(JSON.stringify(FORM_VUOTO));
    setSedi([]);
    try {
      // @api GET /api/ricarica/sedi?societaId -> {success:true,data:[{id,nome}]}
      const res = await apiFetch<{ success: boolean; data: SedeOpzione[] }>(`/api/ricarica/sedi?societaId=${societaId}`);
      setSedi(res.data);
    } catch (err) {
      setErroreForm(messaggioErrore(err, 'Errore nel caricamento delle sedi'));
    }
  };

  const chiudiNuova = () => {
    setForm(null);
    setErroreForm('');
  };

  const salva = async () => {
    if (!form || !societaId) return;
    const nome = form.nome.trim();
    const codice = form.codiceOcpp.trim();
    if (!nome) { setErroreForm('Il nome è obbligatorio'); return; }
    if (!REGEX_CODICE_OCPP.test(codice)) {
      setErroreForm('Codice OCPP: da 3 a 40 caratteri fra lettere maiuscole, cifre, trattino e trattino basso');
      return;
    }
    setSalvando(true);
    setErroreForm('');
    try {
      // @api POST /api/ricarica/colonnine {societaId,nome,codiceOcpp,sedeId} -> {success:true,data:{id}}
      await apiFetch<{ success: boolean; data: { id: number } }>('/api/ricarica/colonnine', {
        method: 'POST',
        body: JSON.stringify({
          societaId: parseInt(societaId, 10),
          nome,
          codiceOcpp: codice,
          sedeId: form.sedeId ? parseInt(form.sedeId, 10) : null,
        }),
      });
      setInitialSnapshot(JSON.stringify(form));
      setForm(null);
      showToast('Colonnina creata: genera la password dalla sua scheda', 'success');
      carica();
    } catch (err) {
      setErroreForm(messaggioErrore(err, 'Errore nella creazione della colonnina'));
    } finally {
      setSalvando(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="bop-ricarica__stack">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">Colonnine</h2>
            <p className="bop-page-subtitle">Colonnine di ricarica collegate al gateway OCPP</p>
          </div>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={apriNuova} disabled={!pronta || !societaId}
              title={societaId ? undefined : 'Scegli una società per creare una colonnina'}>
              + Nuova colonnina
            </button>
          )}
        </div>

        {dati && (gatewayOk ? (
          <div className="bop-alert bop-alert--success bop-mb-0">
            Gateway collegato · ultimo contatto {formatDataOra(dati.gateway.ultimoContattoAt)}
          </div>
        ) : (
          <div className="bop-alert bop-alert--danger bop-mb-0">
            {dati.gateway.ultimoContattoAt
              ? `Gateway non raggiungibile: ultimo contatto ${formatDataOra(dati.gateway.ultimoContattoAt)}. `
              : 'Gateway non raggiungibile: nessun segnale ricevuto finora. '}
            Lo stato delle colonnine non è aggiornato. Le ricariche continuano con i badge salvati sulle colonnine.
          </div>
        ))}

        {errore && <div className="bop-alert bop-alert--danger bop-mb-0">{errore}</div>}

        {dati && (
          <div className="bop-ricarica__kpi-grid">
            <Kpi label="Colonnine collegate" valore={gatewayOk ? `${collegate} su ${visibili.length}` : '—'} />
            <Kpi label="Prese in carica"
              valore={gatewayOk ? String(prese.filter((p) => p.stato === 'Charging').length) : '—'} />
            <Kpi label="Prese guaste"
              valore={gatewayOk ? String(prese.filter((p) => p.stato === 'Faulted').length) : '—'} />
          </div>
        )}

        <GuidaCollegamento apertaIniziale={!!dati && dati.colonnine.length === 0} />

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={(e) => setSocietaId(e.target.value)}>
              <option value="">Tutte le società</option>
              {listaSocieta.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
            </select>
          )}
          <input className="bop-input" placeholder="Cerca nome, codice o sede…" value={fQ}
            onChange={(e) => setFQ(e.target.value)} />
        </div>

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {!pronta ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">Seleziona una società in alto per visualizzare le colonnine</p>
              </div>
            ) : loading && !dati ? (
              <div className="inline-loading">Caricamento…</div>
            ) : visibili.length === 0 ? (
              <div className="bop-empty-state">
                <h3 className="bop-empty-state__title">Nessuna colonnina</h3>
                <p className="bop-empty-state__text">
                  {dati ? 'Nessuna colonnina trovata con i filtri selezionati' : 'Colonnine non disponibili'}
                </p>
              </div>
            ) : (
              <table className="bop-table bop-table--clickable">
                <thead>
                  <tr>
                    <th>Colonnina</th>
                    <th>Società</th>
                    <th>Sede</th>
                    <th>Stato</th>
                    <th>Prese</th>
                    <th>Ultimo contatto</th>
                    <th>Modello</th>
                  </tr>
                </thead>
                <tbody>
                  {visibili.map((c) => (
                    <tr key={c.id} onClick={() => navigate(`${PERCORSO_COLONNINE}/${c.id}`)}>
                      <td>
                        <strong>{c.nome}</strong>
                        <div className="bop-ricarica__secondaria bop-text-mono">{c.codiceOcpp}</div>
                      </td>
                      <td>{c.societaNome}</td>
                      <td className="bop-text-muted">{c.sede?.nome ?? '—'}</td>
                      <td><StatoColonnina colonnina={c} gatewayOk={gatewayOk} /></td>
                      <td><Prese connettori={c.connettori} gatewayOk={gatewayOk} /></td>
                      <td className="bop-text-muted">{formatDataOra(c.ultimoContattoAt)}</td>
                      <td className="bop-text-muted">{c.marca ? `${c.marca} ${c.modello ?? ''}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {form && (
          <div className="bop-modal-overlay" onClick={chiudiNuova}>
            <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h2 className="bop-modal__title">Nuova colonnina</h2>
                <button className="bop-modal__close" onClick={chiudiNuova} aria-label="Chiudi">✕</button>
              </div>
              <div className="bop-modal__body bop-ricarica__form">
                {erroreForm && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreForm}</div>}
                <p className="bop-ricarica__nota">La colonnina viene creata in {nomeSocieta}.</p>
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="col-nome">
                    Nome <span className="bop-field__required">*</span>
                  </label>
                  <input id="col-nome" className="bop-field__input" placeholder="Es. Parcheggio ingresso 3"
                    value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </div>
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="col-codice">
                    Codice OCPP <span className="bop-field__required">*</span>
                  </label>
                  <input id="col-codice" className="bop-field__input bop-text-mono" placeholder="Es. PIA-SEDE-03"
                    value={form.codiceOcpp}
                    onChange={(e) => setForm({ ...form, codiceOcpp: e.target.value.toUpperCase() })} />
                  <p className="bop-field__hint">
                    È l'identificativo che l'installatore configura sulla colonnina: si collegherà a
                    {' '}{URL_GATEWAY}/{form.codiceOcpp || 'CODICE'}
                  </p>
                </div>
                <div className="bop-field">
                  <label className="bop-field__label" htmlFor="col-sede">Sede</label>
                  <select id="col-sede" className="bop-field__select" value={form.sedeId}
                    onChange={(e) => setForm({ ...form, sedeId: e.target.value })}>
                    <option value="">Nessuna sede</option>
                    {sedi.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
                  </select>
                </div>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={chiudiNuova} disabled={salvando}>Annulla</button>
                <button className="bop-btn bop-btn-primary" onClick={salva} disabled={salvando}>
                  {salvando ? 'Salvataggio…' : 'Crea colonnina'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
